import { z } from 'zod'
import { PlanSmokerRateOverrideRuleStatus } from '../enums/plan-smoker-rate-override-rule.enum'

export { US_STATES } from '../../PlanLocationOverrides/components/planLocationOverrideForm.schema'

// Outside the Zod schema — view-only, not editable.
export interface SmokerRateViewMetadata {
  status: PlanSmokerRateOverrideRuleStatus
  createdAt: string
  createdByEmail: string | null
  updatedAt: string
  updatedByEmail: string | null
}

const toNumber = (v: unknown) =>
  v === '' || v === null || v === undefined ? undefined : Number(v)

export const PlanSmokerRateOverrideFormSchema = z.object({
  year: z.preprocess(
    toNumber,
    z
      .number({
        required_error: 'Year is required',
        invalid_type_error: 'Year is required',
      })
      .int()
      .min(2020, 'Year must be 2020 or later')
      .max(2035, 'Year must be 2035 or earlier')
  ),
  state: z.string().min(1, 'State is required'),
  age: z.preprocess(
    toNumber,
    z
      .number({
        required_error: 'Age is required',
        invalid_type_error: 'Age is required',
      })
      .int()
      .min(0, 'Age must be 0 or greater')
      .max(99, 'Age must be 99 or less')
  ),
  issuerId: z
    .string()
    .min(1, 'Issuer ID is required')
    .length(5, 'Issuer ID must be exactly 5 digits')
    .regex(/^\d{5}$/, 'Issuer ID must be exactly 5 digits'),
  increaseRate: z.preprocess(
    toNumber,
    z
      .number({
        required_error: 'Increase Rate is required',
        invalid_type_error: 'Increase Rate is required',
      })
      .min(0, 'Increase Rate must be 0 or greater')
  ),
  isActive: z.boolean({ required_error: 'isActive is required' }),
  sourceUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  notes: z
    .string()
    .max(1000, 'Notes must be 1000 characters or fewer')
    .optional(),
})

export type PlanSmokerRateOverrideFormValues = z.infer<
  typeof PlanSmokerRateOverrideFormSchema
>

export function flatToApiPayload(values: PlanSmokerRateOverrideFormValues) {
  return {
    template: 'UPSERT_RULE' as const,
    match: {
      year: values.year,
      state: values.state,
      age: values.age,
      issuerId: values.issuerId,
    },
    params: {
      increaseRate: values.increaseRate,
      isActive: values.isActive,
    },
    ...(values.sourceUrl ? { sourceUrl: values.sourceUrl } : {}),
    ...(values.notes ? { notes: values.notes } : {}),
  }
}

export function apiPayloadToForm(rule: {
  match: {
    year: number
    state: string
    age: number
    issuerId: string
  }
  params: { increaseRate: number; isActive: boolean }
  sourceUrl: string | null
  notes: string | null
}): PlanSmokerRateOverrideFormValues {
  return {
    year: rule.match.year,
    state: rule.match.state,
    age: rule.match.age,
    issuerId: rule.match.issuerId,
    increaseRate: parseFloat(rule.params.increaseRate.toFixed(3)),
    isActive: rule.params.isActive,
    sourceUrl: rule.sourceUrl ?? '',
    notes: rule.notes ?? '',
  }
}
